import { Grid } from "@mui/material";
import Paper from "@mui/material/Paper";
import AuthService from "Lib/API/Auth";
import Finance from "Lib/API/Finance";
import DonationBox from "components/DonationBox/DonationBox";
import { ContactUs } from "components/ContactUs/ContactUs";
import { useFormik } from "formik";
import { checkIsStripe, checkIsTranzila } from "helper/checkIsStripe";
import { setLocalizationMoment } from "helper/dateFormat";
import { postalCodeFormat, postalCodeLabel } from "helper/postalCodeFormat";
import { useMobileView } from "hook/mobileView/useMobileView";
import { useSnackbar } from "notistack";
import { MemberPersonalDetailsSchema } from "pages/ValidationSchema/index";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router";
import Header from "../Header/Header";
import { ChargeTable } from "./ChargeTableToPay/ChargeTable";
import { PaymentCard } from "./PaymentCard/PaymentCard";
import { PaymentResultPage } from "./PaymentResultPage/PaymentResultPage";
import { PersonalDetailsCard } from "./PersonalDetailsCard/PersonalDetailsCard";
import { SummaryCard } from "./SummaryCard/SummaryCard";
import { CardContent, ImageCard, ImageContainer, Wrapper } from "./Theme";
import { convenienceFeeAmount as calcConvenienceFee } from "helper/convenienceFee";
import {
  getAccessTokenSilently,
  getAuthenticated,
  logout,
} from "utils/functions";

export const ChargeExternalLink = () => {
  const isAuthenticated = getAuthenticated();
  const recaptchaRef = useRef();
  const addCardStripeRef = useRef(null);

  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);
  const [isLoadingPay, setLoadingPay] = useState(false);
  const [checked, setChecked] = useState([]);
  const [chargeSelections, setChargeSelections] = useState([]);
  const [TotalForPay, setTotalForPay] = useState(0);
  const [dataForPay, setDataForPay] = useState([]);
  const queryParameters = new URLSearchParams(window.location.search);
  const participantId = queryParameters.get("participantId");
  const chargeId = queryParameters.get("chargeId");
  const accountId = queryParameters.get("accountId");
  const [error, setError] = useState({ value: true, message: "" });
  const [editedTotalPayment, setEditedTotalPayment] = useState(0);
  const { isMobile } = useMobileView();
  const history = useHistory();
  const [allowEditTotalPayment, setAllowEditTotalPayment] = useState(false);
  const Logo = localStorage.getItem("logoImgSchool");
  const [
    allowInstallmentsOrganization,
    setAllowInstallmentsOrganization,
  ] = useState(false);
  const [paymentMethodExternal, setPaymentMethodExternal] = useState(null);
  const [achPaymentMethodExternal, setAchPaymentMethodExternal] = useState(
    null,
  );
  const [convenienceFeeByPayment, setConvenienceFeeByPayment] = useState({
    "credit-card": true,
    ach: true,
  });
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedAchCard, setSelectedAchCard] = useState(null);
  const [checkoutDonationLedger, setCheckoutDonationLedger] = useState(null);
  const [donationAmount, setDonationAmount] = useState(0);
  const [donationBoxKey, setDonationBoxKey] = useState(0);
  const [hasDonationError, setHasDonationError] = useState(false);
  const [isLoadingDonationLedger, setIsLoadingDonationLedger] = useState(true);
  const hadFailureRef = React.useRef(false);
  const postalLabel = postalCodeLabel();

  const hasInstallmentOption = useCallback(() => {
    const hasInstallmentInCharges = chargeSelections.some(
      (product) => product?.installmentStatus,
    );

    return hasInstallmentInCharges;
  }, [chargeSelections]);
  const hasInstallment = hasInstallmentOption();

  const enableCreditCardPay =
    paymentMethodExternal?.allow_member || !isAuthenticated;
  const enableAchPay = useMemo(
    () =>
      achPaymentMethodExternal?.allow_member &&
      checkIsStripe(achPaymentMethodExternal),
    [achPaymentMethodExternal],
  );
  const disabledCreditCardPay =
    hasInstallment &&
    !paymentMethodExternal?.allowMemberInstallment &&
    isAuthenticated;
  const disabledAchPay =
    (hasInstallment && !achPaymentMethodExternal?.allowMemberInstallment) ||
    (enableAchPay && !isAuthenticated);
  const displayAchTooltip = useMemo(
    () =>
      enableAchPay && !isAuthenticated ? "Please login to pay with ACH" : "",
    [enableAchPay, isAuthenticated],
  );

  const isInstallmentEnabled = useMemo(() => {
    if (!allowInstallmentsOrganization) {
      return false;
    }

    return isAuthenticated
      ? paymentMethodExternal?.allowMemberInstallment ||
          achPaymentMethodExternal?.allowMemberInstallment
      : true;
  }, [
    allowInstallmentsOrganization,
    isAuthenticated,
    paymentMethodExternal,
    achPaymentMethodExternal,
  ]);

  const initialActivePayment = useMemo(() => {
    if (isAuthenticated) {
      if (enableCreditCardPay) return "credit-card";
      if (enableAchPay) return "ach";
      return "";
    }
    return "credit-card";
  }, [isAuthenticated, enableCreditCardPay, enableAchPay]);

  const formik = useFormik({
    isInitialValid: false,
    enableReinitialize: true,
    initialValues: {
      accountInfo: {},
      cardsDetails: {},
      resultPage: "",
      totalPayment: 0,
      activePayment: initialActivePayment,
      achDetails: {},
    },
    validationSchema: MemberPersonalDetailsSchema,
    onSubmit: async (values) => {},
  });

  const activePayment = formik?.values?.activePayment;

  const getCurrentPaymentMethod = useCallback(() => {
    if (formik?.values?.activePayment === "credit-card") {
      return paymentMethodExternal;
    }
    if (formik?.values?.activePayment === "ach") {
      return achPaymentMethodExternal;
    }
    return null;
  }, [formik, paymentMethodExternal, achPaymentMethodExternal]);

  const currentPaymentMethod = useMemo(() => getCurrentPaymentMethod(), [
    getCurrentPaymentMethod,
  ]);
  const isStripe = useMemo(() => checkIsStripe(currentPaymentMethod), [
    currentPaymentMethod,
  ]);
  const isTranzila = useMemo(() => checkIsTranzila(currentPaymentMethod), [
    currentPaymentMethod,
  ]);

  const handleCheck = useCallback(
    (event, value, amount, item) => {
      let updatedListItem = [...chargeSelections];
      let updatedList = [...checked];
      if (event.target.checked) {
        updatedList = [...checked, value];
        updatedListItem = [...chargeSelections, item];
        setTotalForPay(TotalForPay + amount);
      } else {
        updatedList = updatedList?.filter((item) => item !== value);
        updatedListItem = updatedListItem?.filter(
          (val) => val.chargeId !== item.chargeId && item,
        );
        setTotalForPay(TotalForPay - amount);
      }
      setChecked(updatedList);
      setChargeSelections(updatedListItem);
    },
    [chargeSelections, checked, TotalForPay],
  );
  const handleCheckAll = useCallback((data) => {
    if (data?.length > 0) {
      const updatedList = data?.map((val) => val.chargeId);
      setChecked(updatedList);
      const updatedListItem = [...data];
      setChargeSelections(updatedListItem);
      const total = data?.map((val) => val.amount);
      const sum = total.reduce(
        (accumulator, currentValue) => accumulator + currentValue,
        0,
      );
      setTotalForPay(sum);
    } else {
      setChecked([]);
      setChargeSelections([]);
      setTotalForPay(0);
    }
  }, []);

  const getChargeInformation = useCallback(
    async (token, orgID) => {
      const key = chargeId !== null ? "chargeId" : "participantId";
      const value = chargeId !== null ? chargeId : participantId;
      try {
        const res = await Finance.getChargeInfo(
          orgID,
          accountId,
          key,
          value,
          token,
        );
        setDataForPay(res);
        setLoading(false);
      } catch (error) {
        if (
          error?.data?.message ===
          "There is no payment needed now please contact admin"
        ) {
          setDataForPay([]);
          setTotalForPay(0);
        } else {
          history.push(localStorage.getItem("shul_website"));
        }
      } finally {
        setLoading(false);
      }
    },
    [chargeId, participantId, accountId, history],
  );

  const isThereInstallmentPayment = useCallback(() => {
    return chargeSelections?.some((val) => val.installmentStatus);
  }, [chargeSelections]);

  const validInstallments = useCallback(() => {
    const isValidInstallment = chargeSelections?.every((item) => {
      if (item?.installmentStatus) {
        return item?.installment?.value;
      }
      return true;
    });
    return isValidInstallment;
  }, [chargeSelections]);

  const handleGetStripeToken = async () => {
    const token = localStorage.getItem("externalToken");
    const tokenId = await addCardStripeRef?.current?.handleGetToken();
    if (tokenId && tokenId !== null && token !== undefined) {
      return tokenId;
    }
    return "";
  };

  const PayNow = useCallback(
    async (hasStripe) => {
      if (!formik?.values?.activePayment) return;
      const captchaValue = recaptchaRef?.current?.getValue();
      if (!captchaValue && !isAuthenticated) {
        setLoadingPay(false);
        return enqueueSnackbar("Please verify the captcha", {
          variant: "info",
        });
      }

      if (hasDonationError) {
        enqueueSnackbar("Please fix the donation amount before proceeding", { variant: "error" });
        return;
      }

      const validInstallment = validInstallments();
      if (!validInstallment) {
        enqueueSnackbar("Please select number of installment", {
          variant: "info",
        });
        return;
      }
      const validZipCode = postalCodeFormat(formik?.values?.cardsDetails?.zip);

      if (
        !isStripe &&
        formik?.values?.cardsDetails?.zip?.length &&
        !validZipCode &&
        formik?.values?.activePayment === "credit-card" &&
        !isAuthenticated
      ) {
        enqueueSnackbar(`Invalid ${postalLabel}`, { variant: "info" });
      }

      if (
        isAuthenticated &&
        !selectedCard &&
        formik?.values?.activePayment === "credit-card"
      ) {
        enqueueSnackbar("Please select a credit card", { variant: "error" });
        return;
      }

      if (
        isAuthenticated &&
        !selectedAchCard &&
        formik?.values?.activePayment === "ach"
      ) {
        enqueueSnackbar("Please select an ACH card", { variant: "error" });
        return;
      }

      let tokenId = "";
      if (
        isStripe &&
        formik?.values?.activePayment === "credit-card" &&
        !isAuthenticated
      ) {
        tokenId = await handleGetStripeToken();
      }
      console.log("formik?.values?.accountInfo", formik?.values?.accountInfo);
      const data = {
        cardsDetails:
          !tokenId &&
          formik?.values?.activePayment === "credit-card" &&
          !isAuthenticated
            ? {
                object: "card",
                number: formik?.values?.cardsDetails?.card_number,
                exp_month: formik?.values?.cardsDetails?.expiry?.split("/")[0],
                exp_year: formik?.values?.cardsDetails?.expiry?.split("/")[1],
                cvc: formik?.values?.cardsDetails?.cvc,
                zip: formik?.values?.cardsDetails?.zip,
              }
            : undefined,
        token:
          tokenId &&
          formik?.values?.activePayment === "credit-card" &&
          !isAuthenticated
            ? tokenId
            : undefined,
        coverFee: convenienceFeeByPayment[activePayment],
        cardId: isAuthenticated
          ? formik?.values?.activePayment === "credit-card"
            ? selectedCard
            : formik?.values?.activePayment === "ach"
            ? selectedAchCard
            : undefined
          : undefined,
        personalDetails: {
          firstName: formik?.values?.accountInfo?.first_name,
          lastName: formik?.values?.accountInfo?.last_name,
          email: formik?.values?.accountInfo?.email,
        },
        charges: chargeSelections?.map((val) => {
          return {
            chargeId: val?.chargeId,
            numberOfInstallment: val?.installmentStatus
              ? val?.installment?.value
              : undefined,
          };
        }),
        checkoutDonation: donationAmount > 0 && checkoutDonationLedger ? {
          ledger_id: checkoutDonationLedger._id || checkoutDonationLedger.ledger_id,
          ledger_name: checkoutDonationLedger.ledger_name,
          amount: donationAmount,
          account_receivable: checkoutDonationLedger.account_receivable,
        } : undefined,
        accountId: accountId,
        totalPaid: editedTotalPayment
          ? +formik?.values?.totalPayment
          : isThereInstallmentPayment()
          ? dueAmountWithCF
          : undefined,
        paymentMethodId:
          formik?.values?.activePayment === "credit-card"
            ? paymentMethodExternal?._id
            : formik?.values?.activePayment === "ach"
            ? achPaymentMethodExternal?._id
            : undefined,
      };

      if (TotalForPay > 0) {
        if (error?.message) {
          enqueueSnackbar(error?.message, {
            variant: "error",
          });
          setLoadingPay(false);
        } else {
          setLoadingPay(true);
          try {
            const token = isAuthenticated
              ? await getAccessTokenSilently()
              : localStorage.getItem("externalToken");
            await Finance.payExternalLink(
              data,
              token,
              localStorage.getItem("orgId"),
            ).then((res) => {
              formik.setFieldValue("resultPage", "success");
              formik.setFieldValue("email", formik?.values?.accountInfo?.email);
              setTotalForPay(0);
              setChecked([]);
              formik.setFieldValue("accountInfo", {});
              formik.setFieldValue("cardsDetails", {});
              formik.setFieldValue("achDetails", {});
              getChargeInformation(token, localStorage.getItem("orgId"));
            });
          } catch (error) {
            hadFailureRef.current = true;
            formik.setFieldValue("resultPage", "failed");
            formik.setFieldValue("email", "");
          } finally {
            setLoadingPay(false);
          }
        }
      } else {
        enqueueSnackbar("There is no payment to pay", {
          variant: "error",
        });
      }
    },
    [
      checked,
      TotalForPay,
      error,
      formik,
      enqueueSnackbar,
      getChargeInformation,
      editedTotalPayment,
      accountId,
    ],
  );
  const handleTranzilaCharged = useCallback(async (tranzilaResult) => {
    setLoadingPay(true);
    try {
      const token = localStorage.getItem("externalToken");
      const data = {
        tranzilaChargeResult: JSON.stringify(tranzilaResult),
        coverFee: convenienceFeeByPayment[activePayment],
        charges: chargeSelections?.map((val) => ({
          chargeId: val?.chargeId,
          numberOfInstallment: val?.installmentStatus ? val?.installment?.value : undefined,
        })),
        accountId,
        paymentMethodId: paymentMethodExternal?._id,
      };
      await Finance.payExternalLink(data, token, localStorage.getItem("orgId"));
      formik.setFieldValue("resultPage", "success");
      formik.setFieldValue("email", formik?.values?.accountInfo?.email);
      setTotalForPay(0);
    } catch (err) {
      formik.setFieldValue("resultPage", "failed");
    } finally {
      setLoadingPay(false);
    }
  }, [chargeSelections, accountId, convenienceFeeByPayment, activePayment, paymentMethodExternal, formik]);

  const generateToken = useCallback(async () => {
    setLoading(true);
    const subDomain = window.location.hostname.split(".")[0];
    const hostName = subDomain === "localhost" ? (process.env.REACT_APP_LOCAL_ORGKEY || "stagerms") : subDomain;
    try {
      const res = await AuthService.generateToken(hostName);
      setAllowEditTotalPayment(res?.allowEditOnExternal);
      localStorage.setItem("externalToken", res?.token);
      localStorage.setItem("shul_website", res?.shul_website);
      localStorage.setItem("email", res?.contactInfo?.email);
      localStorage.setItem("phone", res?.contactInfo?.phone);
      localStorage.setItem("orgId", res?._id);
      localStorage.setItem("orgName", res?.orgName);
      document.title = `${res?.orgName}`;
      localStorage.setItem("logoImgSchool", res?.logoImg);
      getChargeInformation(res?.token, res?._id);
      localStorage.setItem("country", JSON.stringify(res?.country || {}));
      localStorage.setItem("memberPortal", res?.memberPortal || false);
      setAllowInstallmentsOrganization(res?.allowInstallments || false);
      setPaymentMethodExternal(res?.paymentMethodCreditCard);
      setAchPaymentMethodExternal(res?.paymentMethodAch);

      setLocalizationMoment(res?.country?.local || "en");
    } catch (error) {
      enqueueSnackbar(error?.data?.message, {
        variant: "error",
      });
    }
  }, [enqueueSnackbar, getChargeInformation]);

  useEffect(() => {
    const fetchCheckoutDonationLedger = async () => {
      try {
        const orgToken = isAuthenticated
          ? await getAccessTokenSilently()
          : localStorage.getItem("externalToken");
        const orgId = localStorage.getItem("orgId");
        const res = await Finance.getCheckoutDonationLedger(orgToken, orgId);
        if (res?.ledger) setCheckoutDonationLedger(res.ledger);
      } catch (_) {} finally {
        setIsLoadingDonationLedger(false);
      }
    };
    fetchCheckoutDonationLedger();
  }, []);

  useEffect(() => {
    localStorage.removeItem("redirectUrl");
    generateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      setTimeout(() => {
        const accountDetails = JSON.parse(localStorage.getItem("accountInfo"));
        formik.setFieldValue("accountInfo", {
          first_name: accountDetails?.firstName ?? "",
          last_name: accountDetails?.lastName ?? "",
          primary_email_address: accountDetails?.primary_email_address ?? "",
          email: accountDetails?.primary_email_address ?? "",
        });
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, dataForPay, formik.values?.resultPage]);

  useEffect(() => {
    if (formik.values?.resultPage === "" && hadFailureRef.current) {
      hadFailureRef.current = false;
      setDonationAmount(0);
      setHasDonationError(false);
      setDonationBoxKey((k) => k + 1);
    }
  }, [formik.values?.resultPage]);

  const calculateConvenienceFeeAmount = useCallback(
    (total) => {
      if (!currentPaymentMethod) return 0;
      const shouldApplyConvenienceFee =
        currentPaymentMethod.coverConvenienceFee &&
        convenienceFeeByPayment[activePayment];

      if (!shouldApplyConvenienceFee) return 0;
      return calcConvenienceFee(
        currentPaymentMethod?.convenienceFeeInfo,
        total,
      );
    },
    [currentPaymentMethod, convenienceFeeByPayment, activePayment],
  );

  const reverseCalculationConvenienceFee = useCallback(
    (value) => {
      const totalWithoutConvenience = currentPaymentMethod?.coverConvenienceFee
        ? currentPaymentMethod?.convenienceFeeInfo?.type === "%"
          ? value / (1 + currentPaymentMethod?.convenienceFeeInfo?.amount / 100)
          : value - currentPaymentMethod?.convenienceFeeInfo?.amount
        : value;
      return totalWithoutConvenience;
    },
    [currentPaymentMethod],
  );

  const totalPaymentForCV = useMemo(() => editedTotalPayment || TotalForPay, [
    editedTotalPayment,
    TotalForPay,
  ]);
  const convenienceFeeAmount = useMemo(
    () => calculateConvenienceFeeAmount(totalPaymentForCV),
    [calculateConvenienceFeeAmount, totalPaymentForCV],
  );

  const calculationPayment = useCallback(() => {
    let totalPayment = calculationTotalPaymentWithCV();
    formik.setFieldValue("totalPayment", +totalPayment?.toFixed(2));
    return totalPayment;
  }, [calculateConvenienceFeeAmount, calculationTotalPaymentWithCV, formik]);

  const calculateDueAmount = useCallback(() => {
    const total = chargeSelections?.map((val) => {
      return val?.installmentStatus && val?.installment?.value
        ? val?.amount / val?.installment?.value
        : val?.amount;
    });
    return total.reduce(
      (accumulator, currentValue) => accumulator + currentValue,
      0,
    );
  }, [chargeSelections]);

  const calculateDueAmountWithCF = useCallback(() => {
    const chargesAmount = calculateDueAmount();
    const totalPaymentForCV = editedTotalPayment || chargesAmount;
    const cf = calculateConvenienceFeeAmount(totalPaymentForCV);
    return chargesAmount + (currentPaymentMethod?.coverConvenienceFee ? cf : 0) + donationAmount;
  }, [
    calculateDueAmount,
    donationAmount,
    currentPaymentMethod,
    editedTotalPayment,
    calculateConvenienceFeeAmount,
  ]);

  const calculationTotalPaymentWithCV = useCallback(() => {
    if ((!TotalForPay || TotalForPay <= 0) && donationAmount <= 0) return 0;
    const base = TotalForPay > 0 ? TotalForPay : 0;
    const cf = calculateConvenienceFeeAmount(base);
    return base + cf + donationAmount;
  }, [TotalForPay, donationAmount, calculateConvenienceFeeAmount]);

  useEffect(() => {
    setEditedTotalPayment(0);
    calculationPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked, convenienceFeeByPayment, formik.values?.activePayment]);

  useEffect(() => {
    if (editedTotalPayment === 0) {
      calculationPayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedTotalPayment]);

  useEffect(() => {
    if (editedTotalPayment === 0) {
      calculationPayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donationAmount]);

  const handleLogout = () => {
    formik.setFieldValue("accountInfo", {});
    formik.setFieldValue("activePayment", "credit-card");
    localStorage.clear();
    window.location.reload();
  };

  const setActivePaymentAfter = useCallback(() => {
    if (!isAuthenticated)
      return formik.setFieldValue("activePayment", "credit-card");
    if (
      paymentMethodExternal?.allowMemberInstallment &&
      achPaymentMethodExternal?.allowMemberInstallment
    )
      return;

    const nextPayment = paymentMethodExternal?.allowMemberInstallment
      ? "credit-card"
      : achPaymentMethodExternal?.allowMemberInstallment
      ? "ach"
      : null;

    if (nextPayment) {
      formik.setFieldValue("activePayment", nextPayment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethodExternal, achPaymentMethodExternal, isAuthenticated]);

  const dueAmountWithCF = useMemo(
()=>{
  const cf = currentPaymentMethod?.convenienceFeeInfo?.amount || 0;
      if(!convenienceFeeByPayment[activePayment]&& editedTotalPayment>0){return +formik?.values?.totalPayment}
      // if( editedTotalPayment>-11){ return +formik?.values?.totalPayment}
         if(editedTotalPayment>0){
        return +formik?.values?.totalPayment
      }
      if( convenienceFeeByPayment[activePayment]&&editedTotalPayment<=cf && !isThereInstallmentPayment()){
        return calculateDueAmountWithCF()}
      return calculateDueAmountWithCF()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
   [editedTotalPayment,formik?.values?.totalPayment,calculateDueAmountWithCF,convenienceFeeByPayment[activePayment],donationAmount],
  );

  return (
    <>
      {formik.values?.resultPage !== "" ? (
        <PaymentResultPage
          result={formik.values?.resultPage}
          formik={formik}
          setTotalForPay={setTotalForPay}
          setChecked={setChecked}
          setChargeSelections={setChargeSelections}
        />
      ) : (
        <>
          <Header Logo={Logo} />
          <Wrapper>
            <Grid
              container
              spacing={2}
              sx={{ width: isMobile ? "348px" : "100%" }}
            >
              <Grid item xs={12} sm={12} md={12} lg={7} direction="column">
                <Paper
                  sx={{
                    height: isMobile ? "165px" : "227px",
                    borderRadius: "10px",
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "#1A2027" : "#2555EF",
                  }}
                >
                  <ImageContainer isMobile={isMobile}>
                    <CardContent isMobile={isMobile}>
                      <h1>{dataForPay?.accountInfo?.family_name}</h1>
                      <p>Please see open charges below</p>
                      <span>ID #{dataForPay?.accountInfo?.account_id}</span>
                    </CardContent>
                    <div>
                      <ImageCard isMobile={isMobile} />
                    </div>
                  </ImageContainer>
                </Paper>
                <Paper
                  sx={{
                    height: isMobile
                      ? dataForPay?.charges?.length === 0
                        ? "200px"
                        : "649px"
                      : "649px",
                    marginTop: "33px",
                    borderRadius: "10px",
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "#1A2027" : "#fff",
                  }}
                >
                  <ChargeTable
                    loading={isLoading}
                    list={dataForPay}
                    handleCheck={handleCheck}
                    checkedList={checked}
                    handleCheckAll={handleCheckAll}
                    TotalForPay={TotalForPay}
                  />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={5} direction="column">
                <Paper
                  sx={{
                    minHeight: "201px",
                    maxHeight: "500px",
                    borderRadius: "10px",
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "#1A2027" : "#fff",
                  }}
                >
                  <SummaryCard
                    chargeSelections={chargeSelections}
                    TotalForPay={TotalForPay}
                    currentPaymentMethod={currentPaymentMethod}
                    formik={formik}
                    setEditedTotalPayment={setEditedTotalPayment}
                    editedTotalPayment={editedTotalPayment}
                    calculateConvenienceFeeAmount={
                      calculateConvenienceFeeAmount
                    }
                    reverseCalculationConvenienceFee={
                      reverseCalculationConvenienceFee
                    }
                    calculationTotalPaymentWithCV={
                      calculationTotalPaymentWithCV
                    }
                    allowEditTotalPayment={allowEditTotalPayment}
                    setChargeSelections={setChargeSelections}
                    calculationPayment={calculationPayment}
                    calculateDueAmount={calculateDueAmount}
                    calculateDueAmountWithCF={calculateDueAmountWithCF}
                    dueAmountWithCF={dueAmountWithCF}
                    isThereInstallmentPayment={isThereInstallmentPayment}
                    isInstallmentEnabled={isInstallmentEnabled}
                    setActivePaymentAfter={setActivePaymentAfter}
                    activePayment={activePayment}
                    convenienceFeeByPayment={convenienceFeeByPayment}
                    setConvenienceFeeByPayment={setConvenienceFeeByPayment}
                  />
                </Paper>
                <Paper
                  sx={{
                    minHeight: "278px",
                    marginTop: "33px",
                    borderRadius: "10px",
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "#1A2027" : "#fff",
                  }}
                >
                  <PersonalDetailsCard
                    formik={formik}
                    isAuthenticated={isAuthenticated}
                    handleLogout={handleLogout}
                    logout={logout}
                    showZipCode={!isAuthenticated}
                  />
                </Paper>
                <DonationBox
                  ledger={checkoutDonationLedger}
                  loading={isLoadingDonationLedger}
                  onDonationChange={(amount, ledger) => { setDonationAmount(amount); if (ledger) setCheckoutDonationLedger(ledger); }}
                  resetKey={donationBoxKey}
                  onErrorChange={setHasDonationError}
                />

                <Paper
                  sx={{
                    minHeight: "278px",
                    marginTop: "33px",
                    borderRadius: "10px",
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "#1A2027" : "#fff",
                  }}
                >
                  <PaymentCard
                    TotalForPay={TotalForPay + donationAmount}
                    formik={formik}
                    setError={setError}
                    error={error}
                    PayNow={PayNow}
                    isLoadingPay={isLoadingPay}
                    calculationPayment={calculationPayment}
                    setLoadingPay={setLoadingPay}
                    isStripe={isStripe}
                    isTranzila={isTranzila}
                    currentPaymentMethod={currentPaymentMethod}
                    dueAmountWithCF={dueAmountWithCF}
                    isAuthenticated={isAuthenticated}
                    recaptchaRef={recaptchaRef}
                    setSelectedCard={setSelectedCard}
                    selectedCard={selectedCard}
                    setSelectedAchCard={setSelectedAchCard}
                    selectedAchCard={selectedAchCard}
                    enableCreditCardPay={enableCreditCardPay}
                    enableAchPay={enableAchPay}
                    disabledAchPay={disabledAchPay}
                    displayAchTooltip={displayAchTooltip}
                    disabledCreditCardPay={disabledCreditCardPay}
                    addCardStripeRef={addCardStripeRef}
                    publicKey={
                      currentPaymentMethod?.paymentGateway?.keys?.publicKey
                    }
                    orgId={localStorage.getItem("orgId")}
                    onTranzilaCharged={handleTranzilaCharged}
                  />
                </Paper>
              </Grid>
            </Grid>
            <ContactUs />
          </Wrapper>
        </>
      )}
    </>
  );
};
